import DOMPurify from 'dompurify';

/**
 * Input Sanitization Utility
 * 
 * This utility provides common functions for sanitizing user inputs to prevent
 * XSS (Cross-Site Scripting) attacks and other injection vulnerabilities.
 * 
 * Uses DOMPurify library for HTML sanitization.
 */

/**
 * Configuration for different sanitization contexts
 */
interface SanitizationConfig {
    ALLOWED_TAGS?: string[];
    ALLOWED_ATTR?: string[];
    KEEP_CONTENT?: boolean;
}

/**
 * Predefined sanitization configurations
 */
const SANITIZATION_CONFIGS = {
    // For plain text inputs - removes all HTML tags
    PLAIN_TEXT: {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
        KEEP_CONTENT: true
    } as SanitizationConfig,

    // For rich text editors - allows common formatting tags
    RICH_TEXT: {
        ALLOWED_TAGS: [
            'p', 'br', 'strong', 'em', 'u', 's', 'blockquote',
            'ol', 'ul', 'li', 'a', 'img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
        ],
        ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class']
    } as SanitizationConfig,

    // For search queries - removes all HTML and special characters
    SEARCH_QUERY: {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
        KEEP_CONTENT: true
    } as SanitizationConfig
};

class InputSanitizationUtils {
    
    /**
     * Sanitizes plain text input by removing all HTML tags and potentially dangerous content
     * 
     * @param input - The text input to sanitize
     * @returns Sanitized text string
     */
    public static sanitizePlainText(input: string | undefined | null): string {
        if (!input) return '';
        
        const sanitized = DOMPurify.sanitize(input, SANITIZATION_CONFIGS.PLAIN_TEXT);
        return sanitized.trim();
    }

    /**
     * Sanitizes rich text/HTML content while preserving safe formatting tags
     * Used for content from rich text editors like ReactQuill
     * 
     * @param input - The HTML content to sanitize
     * @returns Sanitized HTML string
     */
    public static sanitizeRichText(input: string | undefined | null): string {
        if (!input) return '';
        
        const sanitized = DOMPurify.sanitize(input, SANITIZATION_CONFIGS.RICH_TEXT);
        return sanitized;
    }

    /**
     * Sanitizes search query input
     * Removes all HTML and potentially dangerous content
     * 
     * @param query - The search query to sanitize
     * @returns Sanitized query string
     */
    public static sanitizeSearchQuery(query: string | undefined | null): string {
        if (!query) return '';
        
        const sanitized = DOMPurify.sanitize(query, SANITIZATION_CONFIGS.SEARCH_QUERY);
        return sanitized.trim();
    }

    /**
     * Sanitizes array of text values
     * Useful for batch sanitization of multiple inputs
     * 
     * @param inputs - Array of text inputs to sanitize
     * @param sanitizationType - Type of sanitization to apply (default: PLAIN_TEXT)
     * @returns Array of sanitized strings
     */
    public static sanitizeTextArray(
        inputs: (string | undefined | null)[],
        sanitizationType: 'PLAIN_TEXT' | 'RICH_TEXT' = 'PLAIN_TEXT'
    ): string[] {
        return inputs.map(input => {
            if (sanitizationType === 'RICH_TEXT') {
                return this.sanitizeRichText(input);
            }
            return this.sanitizePlainText(input);
        });
    }

    /**
     * Sanitizes object properties that contain user input
     * Useful for sanitizing entire objects before storing in database
     * 
     * @param obj - Object with properties to sanitize
     * @param fieldsConfig - Configuration for which fields to sanitize and how
     * @returns New object with sanitized properties
     */
    public static sanitizeObject<T extends Record<string, any>>(
        obj: T,
        fieldsConfig: Record<keyof T, 'PLAIN_TEXT' | 'RICH_TEXT' | 'SKIP'>
    ): T {
        const sanitized = { ...obj };
        
        for (const [key, sanitizationType] of Object.entries(fieldsConfig)) {
            if (sanitizationType === 'SKIP' || !obj[key]) {
                continue;
            }
            
            if (sanitizationType === 'RICH_TEXT') {
                sanitized[key as keyof T] = this.sanitizeRichText(obj[key]) as T[keyof T];
            } else if (sanitizationType === 'PLAIN_TEXT') {
                sanitized[key as keyof T] = this.sanitizePlainText(obj[key]) as T[keyof T];
            }
        }
        
        return sanitized;
    }

    /**
     * Custom sanitization with specific DOMPurify config
     * For advanced use cases requiring custom configuration
     * 
     * @param input - The input to sanitize
     * @param config - Custom DOMPurify configuration
     * @returns Sanitized string
     */
    public static sanitizeCustom(
        input: string | undefined | null,
        config: SanitizationConfig
    ): string {
        if (!input) return '';
        
        const sanitized = DOMPurify.sanitize(input, config);
        return sanitized;
    }

    /**
     * Validates if a string contains potentially dangerous content
     * Returns true if the string appears to contain XSS attempts
     * 
     * @param input - The input to validate
     * @returns true if potentially dangerous content detected
     */
    public static containsPotentialXSS(input: string | undefined | null): boolean {
        if (!input) return false;
        
        // Check for common XSS patterns
        const xssPatterns = [
            /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
            /javascript:/gi,
            /on\w+\s*=/gi, // Event handlers like onclick=
            /<iframe[\s\S]*?>/gi,
            /eval\(/gi,
            /expression\(/gi
        ];
        
        return xssPatterns.some(pattern => pattern.test(input));
    }

    /**
     * Logs sanitization attempt if dangerous content was detected
     * Useful for monitoring and security auditing
     * 
     * @param input - Original input
     * @param sanitized - Sanitized output
     * @param context - Context where sanitization occurred
     */
    public static logSanitization(
        input: string,
        sanitized: string,
        context: string
    ): void {
        if (input !== sanitized) {
            console.warn(`[InputSanitization] Potentially dangerous content detected in ${context}`, {
                original: input.substring(0, 100),
                sanitized: sanitized.substring(0, 100),
                timestamp: new Date().toISOString()
            });
        }
    }
}

export default InputSanitizationUtils;
